import { EventPreview } from "./PageEvents.js";

import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from '@material-ui/core/styles';
import { Button, Chip, Tooltip, Box, Typography } from '@material-ui/core';
import { Edit, CheckCircle, ArrowBack } from '@material-ui/icons';
import Moment from 'react-moment';

import { ViewState } from '@devexpress/dx-react-scheduler';
import {
    Scheduler,
    DayView, MonthView,
    Appointments,
    Toolbar, DateNavigator, AppointmentTooltip, Resources,
    AllDayPanel
} from '@devexpress/dx-react-scheduler-material-ui';

const schedulerResources = [{
    fieldName: 'eventColorGroup',
    title: 'Status',
    instances: [
        { id: 'uow', text: 'Event', color: 'var(--darkBlue)' }
    ],
}];

const calendarStyles = makeStyles((theme) => ({
    appointment: {
        padding: "0.2em",
        height: '100%',
        lineHeight: "1.2em",
        "&:hover": {
            opacity: 0.7
        },
        "& div": {
            whiteSpace: "wrap"
        }
    }
}));

function EventCalendarView({ area, tags, pages, canApprove, canContribute, applyDateFilter, TooltipComponent, dataTransform }) {
    const classes = calendarStyles();

    const [currentDate, setCurrentDate] = useState(new Date());
    const [calendarView, setCalendarView] = useState("Month");

    const [tooltipVisible, setTooltipVisible] = useState(false);

    const schedulerData = useMemo(() => mapPagesToScheduler(pages, dataTransform), [pages]);

    useEffect(() => {
        const baseDate = new Date(currentDate);
        let startRange, endRange;

        if (calendarView === "Month") {
            const firstOfMonth = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
            
            //find starting sunday
            const dayOfWeek = firstOfMonth.getDay(); 
            startRange = new Date(firstOfMonth);
            startRange.setDate(firstOfMonth.getDate() - dayOfWeek);

            //6 rows
            endRange = new Date(startRange);
            endRange.setDate(startRange.getDate() + 41);
            
            startRange.setHours(0, 0, 0, 0);
            endRange.setHours(23, 59, 59, 999);
        } else if (calendarView === "Day") {
            startRange = new Date(baseDate.setHours(0, 0, 0, 0));
            endRange = new Date(baseDate.setHours(23, 59, 59, 999));
        }

        applyDateFilter?.({ start: startRange, end: endRange });
    }, [currentDate, calendarView]);

    const TimeTableCellWrapper = useMemo(() => {
        return (props) => (
            <ClickableTimeTableCell
                {...props}
                onCellClick={(clickedDate) => {
                    setCurrentDate(clickedDate);
                    setCalendarView("Day");
                }}
            />
        );
    }, []);

    const datelessPages = useMemo(() => pages.filter(p => !p.eventDates || p.eventDates.length == 0), [pages]);

    const CustomToolbarFlexibleSpace = useMemo(() => {
        return (props) => (
            <Toolbar.FlexibleSpace {...props}>
                <div style={{ marginLeft: 'auto', paddingRight: '16px' }}>
                    {calendarView === "Day" && (
                        <Button variant="outlined" color="primary" size="small"
                            startIcon={<ArrowBack />} onClick={() => setCalendarView("Month")} >
                            Month View</Button>
                    )}
                </div>
            </Toolbar.FlexibleSpace>
        );
    }, [calendarView]);

    const TooltipContentWrapper = useMemo(() => {
        if(TooltipComponent)
            return (props) => <TooltipComponent onTooltipClose={() => setTooltipVisible(false)} canApprove={canApprove} canContribute={canContribute} {...props} />
        return (props) => <EventPreview embedded={true} {...props} page={props.appointmentData.data} tags={tags} area={area} canApprove={canApprove} canContribute={canContribute} />;
    }, [TooltipComponent, canApprove, canContribute, tags, area]);

    return <Scheduler data={schedulerData} className={classes.calendar}>
            <ViewState currentDate={currentDate} onCurrentDateChange={setCurrentDate} currentViewName={calendarView} onCurrentViewNameChange={setCalendarView} />
            <MonthView timeTableCellComponent={TimeTableCellWrapper} />
            <DayView cellDuration={60} startDayHour={8} endDayHour={19} />
            <Appointments appointmentComponent={CustomAppointment} />
            <AllDayPanel />
            <AppointmentTooltip contentComponent={TooltipContentWrapper} visible={tooltipVisible} onVisibilityChange={setTooltipVisible} style={{background: 'red'}} />
            <Resources data={schedulerResources} mainResourceName="eventColorGroup" />
            <Toolbar flexibleSpaceComponent={CustomToolbarFlexibleSpace} />
            <DateNavigator />
        </Scheduler>;
}


function mapPagesToScheduler(pages, dataTransform = (x) => x) {
    const output = [];
    pages.forEach(p => {
        if (!p.eventDates)
            return;
        p.eventDates.forEach((e,i) => {
            output.push(dataTransform({
                id: p.id+" "+i,
                title: p.title,
                startDate: e.eventStartDate,
                endDate: e.eventEndDate,
                eventColorGroup: 'uow',
                data: p
            }));
        });
    });
    return output;
}

function CustomAppointment({ children, data, ...restProps }) {
    const classes = calendarStyles();
    return (
        <Appointments.Appointment data={data} {...restProps} className={classes.appointment}>
            <Tooltip title={<><Moment format="h.mm a">{data.startDate}</Moment> {data.title}</>}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: '6px', height: '100%' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'wrap', height: '100%' }}>
                        <Moment format="h.mm a" style={{ color: 'white', paddingLeft: '6px' }} >{data.startDate}</Moment>
                        {children}
                    </div>
                    {data.hasAttended && (
                        <CheckCircle style={{ color: '#4caf50', fontSize: '14px', marginLeft: '4px', flexShrink: 0 }} />
                    )}
                </div>
            </Tooltip>
        </Appointments.Appointment>
    );
}

function ClickableTimeTableCell({ onCellClick, ...restProps }) {
    const { startDate } = restProps;
    return <MonthView.TimeTableCell {...restProps} onClick={() => onCellClick?.(startDate)} style={{ cursor: 'pointer' }} />;
}
export default EventCalendarView;
